import { makeAutoObservable } from 'mobx'
import Keycloak from 'keycloak-js'
import { UserFullInfo } from '@entities/User/types/user.fullInfo'
import axios from 'axios'
import { mapKcToUserInfo } from '@entities/User/lib/mappers/kcToUserInfo'
import { mapUserInfoToKc } from '@entities/User/lib/mappers/userInfoToKc'
import { toast } from 'sonner'
import { URLS } from '@assets/environment/urls'
import { KeycloakGetResponse } from '@entities/User/types/kc.get'
import { AvatarInfo } from '@entities/User/types/avatar'
import { authAxios } from '@shared/api/auth-instance'

type AccessTokenProvider = () => Promise<string | null>
type UpdateDataOptions = {
	silent?: boolean
}
type ProfileDataUpdatedCallback = (data: Partial<UserFullInfo>) => void

const withoutUndefinedValues = (data: Partial<UserFullInfo>): Partial<UserFullInfo> =>
	Object.fromEntries(
		Object.entries(data).filter(([, value]) => value !== undefined)
	) as Partial<UserFullInfo>

export class UserService {
	private data_: Partial<UserFullInfo> = {}
	private readonly keycloak: Keycloak | null
	private readonly accessTokenProvider: AccessTokenProvider | null
	private wasInitialized: boolean = false
	private avatarUrl_: string | undefined = undefined
	private accessToken_: string | null = null
	private profileStatus_: 'idle' | 'loading' | 'loaded' | 'failed' = 'idle'
	private profileDataUpdatedCallback_: ProfileDataUpdatedCallback | null = null
	private profileMutationVersion_: number = 0

	constructor(keycloak: Keycloak | null, accessTokenProvider?: AccessTokenProvider) {
		makeAutoObservable(this)
		this.keycloak = keycloak
		this.accessTokenProvider = accessTokenProvider ?? null
	}

	public get data(): Partial<UserFullInfo> {
		return this.data_
	}

	public get avatarUrl(): string | undefined {
		return this.avatarUrl_
	}

	public get isProfileLoading(): boolean {
		return this.profileStatus_ === 'loading'
	}

	public get isProfileReady(): boolean {
		return this.profileStatus_ === 'loaded'
	}

	public get hasResolvedProfile(): boolean {
		return this.profileStatus_ === 'loaded' || this.profileStatus_ === 'failed'
	}

	private base64UrlDecode = (str: string): string => {
		str = str.replace(/-/g, '+').replace(/_/g, '/')
		while (str.length % 4) {
			str += '='
		}
		return atob(str)
	}

	private applyAvatar = (avatar: AvatarInfo) => {
		this.avatarUrl_ = avatar.url
		this.data_ = { ...this.data_, avatarUrl: avatar.url }
	}

	public setAvatar = (avatar: AvatarInfo) => {
		this.applyAvatar(avatar)
		this.markProfileDataChanged()
		this.notifyProfileDataUpdated()
	}

	private getCurrentAccessToken = async (): Promise<string | null> => {
		if (this.keycloak) {
			await this.keycloak.updateToken(5)
			return this.keycloak.token ?? null
		}

		if (this.accessTokenProvider) {
			const freshAccessToken = await this.accessTokenProvider()
			this.setToken(freshAccessToken ?? undefined)
			return freshAccessToken
		}

		return this.accessToken_
	}

	public setToken = (token: string | undefined) => {
		if (!token) {
			this.accessToken_ = null
			return
		}
		this.accessToken_ = token
		try {
			const [, payload] = token.split('.')
			const decodedPayload = this.base64UrlDecode(payload)
			this.data_ = { ...JSON.parse(decodedPayload), ...this.data_ }
		} catch {
			// Token claims are optional here; malformed credentials must not be logged.
		}
	}

	public setProfileDataUpdatedCallback = (callback: ProfileDataUpdatedCallback | null) => {
		this.profileDataUpdatedCallback_ = callback
	}

	private notifyProfileDataUpdated = () => {
		this.profileDataUpdatedCallback_?.(this.data_)
	}

	private markProfileDataChanged = () => {
		this.profileMutationVersion_ += 1
	}

	public updateData = async (
		data: Partial<UserFullInfo>,
		options: UpdateDataOptions = {}
	): Promise<boolean> => {
		if (!this.data_) {
			return false
		}
		const nextData = { ...this.data_, ...data }
		this.data_ = nextData
		this.markProfileDataChanged()
		const success = await this.sendNewDataInKc(nextData, options)

		if (success) {
			this.wasInitialized = true
			this.profileStatus_ = 'loaded'
			this.notifyProfileDataUpdated()
		}

		return success
	}

	public setData = (data: Partial<UserFullInfo>) => {
		this.data_ = { ...this.data_, ...data }
		this.markProfileDataChanged()
		this.notifyProfileDataUpdated()
	}

	public hydrateProfileData = (data: Partial<UserFullInfo>) => {
		const normalizedData = withoutUndefinedValues(data)
		this.data_ =
			this.profileMutationVersion_ > 0
				? { ...normalizedData, ...this.data_ }
				: { ...this.data_, ...normalizedData }
		this.wasInitialized = true
		this.profileStatus_ = 'loaded'
	}

	public refreshProfileData = async (): Promise<Partial<UserFullInfo>> => {
		this.profileStatus_ = 'loading'
		const profile = await this.loadProfile()
		const mappedProfile = mapKcToUserInfo(profile)
		const avatarData = this.avatarUrl_ ? { avatarUrl: this.avatarUrl_ } : {}

		this.data_ = {
			...this.data_,
			...withoutUndefinedValues(mappedProfile),
			...avatarData,
		}
		this.wasInitialized = true
		this.profileStatus_ = 'loaded'
		this.notifyProfileDataUpdated()

		return this.data_
	}

	public sendNewDataInKc = async (
		data: Partial<UserFullInfo> = this.data_,
		options: UpdateDataOptions = {}
	): Promise<boolean> => {
		const requestData = mapUserInfoToKc(data)
		const accessToken = await this.getCurrentAccessToken()

		if (!accessToken) {
			if (!options.silent) {
				toast.error('Нет активной сессии для обновления профиля', {
					position: 'top-center',
				})
			}
			return false
		}

		const authServerUrl = this.keycloak?.authServerUrl ?? URLS.KC_URL
		const realm = this.keycloak?.realm ?? URLS.KC_REALM
		if (!authServerUrl || !realm) {
			if (!options.silent) {
				toast.error('Нет конфигурации авторизации для обновления профиля', {
					position: 'top-center',
				})
			}
			return false
		}
		try {
			const accountUrl = `${authServerUrl}realms/${realm}/account`
			if (this.keycloak) {
				await axios.post(accountUrl, requestData, {
					headers: {
						'Authorization': `Bearer ${accessToken}`,
						'Content-Type': 'application/json',
					},
				})
			} else {
				await authAxios.post(accountUrl, requestData, {
					headers: {
						'Content-Type': 'application/json',
					},
				})
			}
			if (!options.silent) {
				toast.success('Ваши данные успешно отправлены', {
					position: 'top-center',
				})
			}
			return true
		} catch {
			if (!options.silent) {
				toast.error('Кажется, произошла ошибка при отправке запроса', {
					position: 'top-center',
				})
			}
			return false
		}
	}

	public getUserProfileData = async (): Promise<Partial<UserFullInfo>> => {
		if (this.wasInitialized || this.profileStatus_ === 'loading') {
			return this.data_
		}

		try {
			this.profileStatus_ = 'loading'
			const loadStartedAtMutationVersion = this.profileMutationVersion_
			const profile = await this.loadProfile()

			const mappedProfile = mapKcToUserInfo(profile)
			const avatarData = this.avatarUrl_ ? { avatarUrl: this.avatarUrl_ } : {}
			const hasLocalChangesDuringLoad =
				loadStartedAtMutationVersion !== this.profileMutationVersion_

			this.data_ = hasLocalChangesDuringLoad
				? {
						...mappedProfile,
						...this.data_,
						...avatarData,
					}
				: {
						...this.data_,
						...mappedProfile,
						...avatarData,
					}
			this.wasInitialized = true
			this.profileStatus_ = 'loaded'
		} catch (error: unknown) {
			this.profileStatus_ = 'failed'
			throw error
		}

		return this.data_
	}

	private loadProfile = async (): Promise<KeycloakGetResponse> => {
		if (this.keycloak) {
			await this.keycloak.updateToken(5)
			return (await this.keycloak.loadUserProfile()) as KeycloakGetResponse
		}

		const accessToken = await this.getCurrentAccessToken()
		if (!accessToken) {
			throw new Error('Нет access token для загрузки профиля')
		}

		const response = await authAxios.get<KeycloakGetResponse>(
			`${URLS.KC_URL}realms/${URLS.KC_REALM}/account`
		)

		return response.data
	}
}
